/**
 * Run `worker` over `items` with at most `limit` promises in flight at once.
 *
 * Collects every outcome the way `Promise.allSettled` does — a single rejection
 * never aborts or blocks the others, and the remaining items keep going until
 * they all settle. Results are index-aligned with `items`.
 *
 * Wall-clock is bounded by the concurrency `limit`, not the sum of every
 * worker's duration: with `limit` lanes, N items of roughly equal cost take
 * about `ceil(N / limit)` rounds rather than N sequential awaits.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length)
  const lanes = Math.max(1, Math.min(limit, items.length))
  let next = 0

  const run = async () => {
    while (next < items.length) {
      const index = next++
      try {
        results[index] = { status: 'fulfilled', value: await worker(items[index], index) }
      } catch (reason) {
        results[index] = { status: 'rejected', reason }
      }
    }
  }

  await Promise.all(Array.from({ length: lanes }, run))
  return results
}
